/**
 * Server shell lifecycle — the shell rule (R15, "Regola del guscio").
 *
 * The server-rendered `#ssr-content` shell (T007-BE/T008-BE) may only leave
 * the page once the client-rendered app actually shows real content — never
 * on mount, and never because a request merely finished. Removing it earlier
 * would swap content -> blank/wrong -> content (forbidden by
 * IMPLEMENTATION_PLAN.md, "Il guscio server e la homepage: come convivono")
 * or, worse, leave the page with neither the shell nor the app: that is what
 * happens today when `/api/*` is blocked by robots.txt for Google or the API
 * is simply down, and the page ends up with no H1 and no events at all.
 *
 * The rule per caller (each caller passes its own outcome):
 *
 * - Event page (`EventPage`, `EventModal`): the event is loaded, settled and
 *   error-free.
 * - List pages (`HomePage`, `EsploraPage`): the territory resolution has
 *   settled AND is present, AND the app draws at least one event — or the
 *   shell itself links to no `/event/` (an empty shell carries nothing worth
 *   keeping, so the app may take over).
 *
 * In every other case — API down, response slow, failed resolution, empty
 * list against a shell that still links to events — the shell stays visible
 * on top of the app: it is then the only real content of the page.
 *
 * **Cross-route half (B1):** `#ssr-content` lives outside `#root` and
 * survives client navigation, so a shell *kept* on route A must not stay on
 * top of route B, which may be drawing real data (`resolution === null` on
 * `/` and `/esplora` would otherwise block its release forever). The shell
 * belongs to the document the server rendered it for: the moment the current
 * pathname stops matching it, the shell is foreign and `removeSsrShell` goes
 * through unconditionally — see `useSsrShellRouteGuard`, the single point
 * that reacts to route changes for every public route.
 *
 * A no-op when the server didn't render a shell for this route (`#ssr-content`
 * is absent) and a no-op if it was already removed — safe to call from every
 * route, territory or not.
 *
 * Waits two animation frames before removing: the first lets the browser
 * commit whatever React just rendered with real data, the second guarantees
 * it landed in the DOM before the shell disappears.
 *
 * **Handoff scaffold (T102-INT):** the server document ships with
 * `body[data-ssr-handoff="pending"]` and an `inert`, invisible `#root` that
 * shares ONE grid cell with the shell, aligned to the top
 * (`api/lib/render/html.ts`). The app therefore mounts exactly where it will
 * stay, and releasing is a single synchronous DOM commit in the second frame:
 * shell removed, `inert` removed, attribute removed — then the release
 * listeners fire (`subscribeSsrRootRelease`). Nothing moves, so no layout
 * shift. A document without the scaffold (older cached HTML, or a page with
 * no shell such as today's home) keeps the plain behaviour above and counts
 * as already released.
 *
 * **Interaction preservation:** if the user scrolled the document or put
 * focus inside the shell before readiness (`armSsrShellInteractionGuard`,
 * latched, re-checked at commit time) the shell is KEPT for this document —
 * removing it would pull the page they are reading from under them. The root
 * is still exposed, now below the shell: exactly the pre-scaffold layout, and
 * the root was invisible, so no visible element moves. A kept shell is
 * ignored by later readiness calls; only `{ force: true }` removes it.
 *
 * `force` is the cross-route half: a pathname foreign to the shell wins over
 * interaction preservation (`useSsrShellRouteGuard`).
 *
 * Idempotent: any number of calls (StrictMode double effects, several shell
 * owners) schedules one frame chain per shell and notifies once; a `force`
 * request arriving while a release is pending upgrades it.
 */
export function removeSsrShell(options: RemoveSsrShellOptions = {}): void {
  const shell = document.getElementById(SHELL_ID);
  if (!shell) {
    // No shell left but the scaffold still hides the root: never leave the
    // app invisible with nothing in its place.
    if (isHandoffPending()) exposeRoot();
    return;
  }

  const pendingRelease = scheduledReleases.get(shell);
  if (pendingRelease) {
    pendingRelease.force = pendingRelease.force || options.force === true;
    return;
  }
  if (keptShells.has(shell) && options.force !== true) return;

  const release = { force: options.force === true };
  scheduledReleases.set(shell, release);
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      scheduledReleases.delete(shell);
      commitRelease(shell, release.force);
    });
  });
}

export interface RemoveSsrShellOptions {
  /** Release even if the user interacted with the shell (foreign pathname). */
  force?: boolean;
}

// --- Handoff scaffold ------------------------------------------------------

const SHELL_ID = 'ssr-content';
const ROOT_ID = 'root';
// Contract with `api/lib/render/html.ts` (HANDOFF_ATTRIBUTE / _STATE_PENDING).
const HANDOFF_ATTRIBUTE = 'data-ssr-handoff';
const HANDOFF_STATE_PENDING = 'pending';

/** One scheduled release per shell (WeakMap: a replaced shell starts clean). */
const scheduledReleases = new WeakMap<Element, { force: boolean }>();
/** Shells deliberately kept because the user interacted with them. */
const keptShells = new WeakSet<Element>();
const releaseListeners = new Set<() => void>();

let userUsedShell = false;
let disarmInteractionGuard: (() => void) | null = null;

function isHandoffPending(): boolean {
  return (
    typeof document !== 'undefined' &&
    document.body?.getAttribute(HANDOFF_ATTRIBUTE) === HANDOFF_STATE_PENDING
  );
}

/**
 * Is the app root usable? `false` only while the server scaffold still hides
 * it; `true` for documents without the scaffold. Snapshot for
 * `useSyncExternalStore` together with `subscribeSsrRootRelease`.
 */
export function isSsrRootReleased(): boolean {
  return !isHandoffPending();
}

/**
 * Be told when the root stops being hidden (the shell removed or kept, `inert`
 * gone). Consumers that must not act on an invisible app — the desktop event
 * modal's scroll lock and ESC — wait for it. Returns the unsubscribe function.
 */
export function subscribeSsrRootRelease(listener: () => void): () => void {
  releaseListeners.add(listener);
  return () => {
    releaseListeners.delete(listener);
  };
}

/** Has the user scrolled the document or focused something inside the shell? */
function shellWasUsed(shell: Element): boolean {
  return userUsedShell || window.scrollY > 0 || shell.contains(document.activeElement);
}

/**
 * Start watching for the two interactions that make the shell worth keeping:
 * scrolling the document and focusing inside the shell. Called once at module
 * load (this module is in the entry chunk, so it runs as soon as the script
 * does) and again by tests. What happened BEFORE the script ran — scroll
 * restoration, a focused link — is read on the spot. Only armed while the
 * scaffold is pending; every release path disarms it, so no listener outlives
 * the handoff. Idempotent.
 */
export function armSsrShellInteractionGuard(): void {
  if (disarmInteractionGuard || !isHandoffPending()) return;
  const shell = document.getElementById(SHELL_ID);
  if (!shell) return;
  if (shellWasUsed(shell)) {
    userUsedShell = true;
    return;
  }

  const latch = () => {
    userUsedShell = true;
    disarmInteractionGuard?.();
  };
  const onScroll = () => {
    if (window.scrollY > 0) latch();
  };
  const onFocusIn = (event: FocusEvent) => {
    const current = document.getElementById(SHELL_ID);
    if (current && event.target instanceof Node && current.contains(event.target)) latch();
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  document.addEventListener('focusin', onFocusIn);
  disarmInteractionGuard = () => {
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('focusin', onFocusIn);
    disarmInteractionGuard = null;
  };
}

/**
 * The app reached a state it cannot leave on its own — a blocking upgrade, an
 * unavailable or invalid territory (`DiscoveryEpochNotice`) — whose notice and
 * action (Aggiorna, Riprova) would otherwise stay hidden behind the shell.
 * Expose the root below the shell WITHOUT keeping the shell: when the page
 * later becomes ready (a successful retry) the normal release still removes
 * it, as before the scaffold. Nothing visible moves now — the root was
 * invisible and the shell stays where it is. No-op without a pending scaffold.
 */
export function exposeSsrRoot(): void {
  if (isHandoffPending()) exposeRoot();
}

/**
 * Expose the root: remove `inert` and the pending attribute in the same
 * synchronous block, then notify. `commitRelease` calls it after removing (or
 * deliberately keeping) the shell in that same block; `exposeSsrRoot` and the
 * shell-less branch of `removeSsrShell` call it with the shell left as is.
 */
function exposeRoot(): void {
  document.getElementById(ROOT_ID)?.removeAttribute('inert');
  document.body.removeAttribute(HANDOFF_ATTRIBUTE);
  disarmInteractionGuard?.();
  for (const listener of [...releaseListeners]) {
    try {
      listener();
    } catch (error) {
      // The DOM is already committed; one faulty subscriber must not stop the others.
      console.error('ssrShell: release listener failed', error);
    }
  }
}

/** The single atomic DOM commit of a release (second animation frame). */
function commitRelease(shell: HTMLElement, force: boolean): void {
  const pending = isHandoffPending();
  const keep = !force && (keptShells.has(shell) || (pending && shellWasUsed(shell)));
  if (keep) keptShells.add(shell);
  else shell.remove();
  if (pending) exposeRoot();
  disarmInteractionGuard?.();
}

armSsrShellInteractionGuard();

/**
 * Pathname of the document the server rendered the shell for.
 *
 * Captured at module load: this module is in the entry chunk (App →
 * EventModal → ssrShell) and evaluates before `main.tsx` mounts the router,
 * so this really is the initial pathname. A capture taken *after* a
 * navigation would fail safe: the shell would count as belonging to the
 * current document and be kept, never wrongly removed.
 */
const SSR_SHELL_PATHNAME = normalizeShellPathname(
  typeof window === 'undefined' ? '/' : window.location.pathname
);

/**
 * Canonical form for the shell comparison: everything before `?`/`#`,
 * trailing slashes collapsed (`/eventi/cremona/` ≡ `/eventi/cremona`), root
 * kept as `/`. Nothing else is rewritten — both sides are the raw
 * `window.location.pathname`, and a canonicalisation of our own could mask a
 * real move to a different document. Query strings stay out on purpose: the
 * Esplora bootstrap strips URL params with `replace` on the page the user is
 * still on.
 */
function normalizeShellPathname(pathname: string): string {
  const path = pathname.split(/[?#]/)[0] ?? '';
  const stripped = path.replace(/\/+$/, '');
  return stripped === '' ? '/' : stripped;
}

/**
 * Shell rule (R15), cross-route half: is a shell on screen foreign to
 * `pathname` — rendered for a different document? Pure pathname comparison
 * (see `normalizeShellPathname`), no DOM. When `false`, the per-route
 * conditions above decide; when `true`, the shell must go whatever the
 * route's own data state is.
 */
export function isSsrShellForeign(pathname: string): boolean {
  return normalizeShellPathname(pathname) !== SSR_SHELL_PATHNAME;
}

/**
 * Does the server shell currently link to any event page (`a[href^="/event/"]`)?
 *
 * The shell's side of the rule above: a shell that still links to events is
 * full, so an app that draws nothing must not cover it. Returns `false` when
 * there is no shell at all — nothing to keep, callers can take the page over.
 */
export function ssrShellHasEventLinks(): boolean {
  const shell = document.getElementById('ssr-content');
  if (!shell) return false;
  return shell.querySelector('a[href^="/event/"]') !== null;
}

/**
 * Title of the server's date-review page (T009-BE `renderEventDateReview`,
 * `api/ssr.ts:288`) — mirrored here so the client-side scrub keeps the same
 * headline as the authoritative 503 document.
 */
export const SSR_DATE_REVIEW_TITLE = 'Questo evento è in verifica';

/**
 * Scrub the dated `<head>` of a cached event document (T010-FE, B1).
 *
 * The only document that can boot the client into the review state is a
 * stale cached 200 (a fresh SSR answers 503 with a clean review page), and
 * that document's head is dated too: `<title>` carries the event date
 * (`api/lib/render/html.ts`), description/og/twitter metas repeat it, and
 * the Event JSON-LD with `startDate` sits in a `application/ld+json`
 * script. `removeSsrShell` only removes the body div, so without this the
 * browser tab and the remaining DOM keep advertising the disputed date
 * after the notice settles — exactly what the T010 simulation forbids.
 *
 * Guard: runs only when the BOOTING document is an event page, reusing the
 * module-captured `SSR_SHELL_PATHNAME` (same family as `isSsrShellForeign`).
 * A client-side navigation from a territory/list page must never wipe that
 * page's head — its head carries no event date and belongs to its route.
 *
 * Idempotent: safe to call on every review render; the robots meta is
 * created once and then only updated.
 */
export function clearDisputedDateHead(): void {
  if (!SSR_SHELL_PATHNAME.startsWith('/event/')) return;

  document.title = SSR_DATE_REVIEW_TITLE;

  document.querySelector('meta[name="description"]')?.remove();
  document
    .querySelectorAll('meta[property^="og:"], meta[name^="twitter:"]')
    .forEach((meta) => meta.remove());
  // The Event schema carries startDate — it must not survive either.
  document
    .querySelectorAll('script[type="application/ld+json"]')
    .forEach((script) => script.remove());

  // Mirror the 503 review page's noindex for the cached-200 window.
  let robots = document.querySelector('meta[name="robots"]');
  if (!robots) {
    robots = document.createElement('meta');
    robots.setAttribute('name', 'robots');
    document.head.appendChild(robots);
  }
  robots.setAttribute('content', 'noindex');
}
